import React, { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import AdvancedQuoteFields, { emptyQuoteData, buildQuotePayload } from '../components/AdvancedQuoteFields';
import { submitAdvancedQuote } from '../utils/api';

const AdvancedQuote = () => {
  const [data, setData] = useState(emptyQuoteData());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const validate = () => {
    if (!data.firstName.trim() || !data.lastName.trim()) { toast.error('Please enter your first and last name'); return false; }
    if (!data.email.trim() || !/^\S+@\S+\.\S+$/.test(data.email)) { toast.error('Please enter a valid email'); return false; }
    if (!data.phone.trim()) { toast.error('Please enter your phone number'); return false; }
    if (!data.state.trim()) { toast.error('Please select your state'); return false; }
    if (!data.zipCode.trim()) { toast.error('Please enter your ZIP code'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitAdvancedQuote(buildQuotePayload(data));
      if (res.success) { setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }
      else toast.error('Something went wrong. Please try again.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="advanced-quote-success">
        <div className="max-w-lg text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-10">
          <CheckCircle2 className="mx-auto text-green-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank you, {data.firstName}!</h1>
          <p className="text-gray-600">Your detailed quote request has been received. David Ashley will personally review your information and reach out shortly with the best options for you.</p>
          <p className="text-sm text-gray-400 mt-4">Prefer to talk now? Call (912) 376-9006.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="advanced-quote-page">
      <div className="bg-brand-blue text-white py-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <ShieldCheck className="mx-auto mb-3" size={40} />
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Get Your Personalized Quote</h1>
          <p className="text-white/85 text-base max-w-2xl mx-auto">
            Share a few details so we can shop multiple carriers and find the most accurate, best-priced coverage for you and your family.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <AdvancedQuoteFields data={data} setData={setData} />

        <button onClick={handleSubmit} disabled={submitting}
          className="mt-6 w-full bg-brand-orange text-white font-bold py-4 rounded-xl text-lg hover:bg-orange-600 transition disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="advanced-quote-submit">
          {submitting ? <><Loader2 className="animate-spin" size={20} /> Submitting…</> : 'Submit My Quote Request'}
        </button>
        <p className="text-center text-xs text-gray-400 mt-3">Your information is secure and confidential.</p>
      </div>
    </div>
  );
};

export default AdvancedQuote;
